import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from '../../store'
import { PrivateOffer } from '../../modules/privateOffer/types'
import { LoadingLogo, PlanType } from '@labrav/react-components'
import { ContainerPricingModel } from '../../modules/productsListing/types'
import { useEffect, useRef } from 'react'
import { isEmpty } from 'lodash'
import { getCurrentPrivateOffer } from '../../modules/privateOffer/action'
import {
  DetailedViewPrivateOffer,
  type DetailedViewPrivateOfferProps,
} from './DetailedViewPrivateOffer/DetailedViewPrivateOffer'
import {
  useAccountId,
  useValidateCloudRoute,
  LoadingTypes,
  isLoading as Loading,
  useCurrentCloudProvider,
  useGetAllUsers,
  useUserType,
} from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import { CRMTypes } from '../../utils/constants'
import { getProducts } from '../../modules/productsListing/action'
import { useGetProductDetail } from '../../common/utils/hooks/useGetProductDetail'
import { usePrivateOfferViewTracking } from '../../common/utils/hooks/usePrivateOfferCreationTracking'

export type DetailedViewPrivateOfferWrapperProps = {
  crmType?: CRMTypes
  crmOpportunityId?: string
  externalBaseURL?: string
  search?: string
  onBack?: () => void
  partnerId?: string
}

export const DetailedViewPrivateOfferWrapper: React.FC<
  DetailedViewPrivateOfferWrapperProps
> = ({
  crmOpportunityId,
  externalBaseURL,
  search,
  crmType,
  onBack,
  partnerId,
}) => {
  useSyncFlyoutRouteParams()

  const dispatch = useDispatch()
  const partnerType = useUserType()
  const { users } = useGetAllUsers(partnerType)
  const viewTracking = usePrivateOfferViewTracking()

  const selectedCloudType = useCurrentCloudProvider()
  const {
    accountId: routeAccountId,
    productId,
    privateOfferId,
    partnerId: partnerIdFromParams,
  } = useParams<{
    cloud: string
    accountId: string
    productId: string
    privateOfferId: string
    partnerId: string
  }>()

  const { isLoading: productLoading } = useSelector<{
    isLoading: boolean
  }>(state => ({
    isLoading: Loading(state.loading)(LoadingTypes.FETCH_PRODUCTS),
  }))

  const isOfferLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GENERAL)
  )

  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const currentAccountId =
    routeAccountId ||
    partnerId ||
    fallbackAccountId ||
    partnerIdFromParams ||
    ''
  const hasValidAccountId = routeAccountId
    ? !isEmpty(routeAccountId)
    : !isEmpty(fallbackAccountId) ||
      !isEmpty(partnerId) ||
      !isEmpty(partnerIdFromParams)
  const initiatedFetchRef = useRef<string | null>(null)

  useValidateCloudRoute('flyout', '/flyout', !!externalBaseURL)

  const selectedProduct = useGetProductDetail({
    accountId: currentAccountId,
    productId: productId || '',
    cloudMarketplace: selectedCloudType,
  })

  const { privateOffer } = useSelector<{ privateOffer: PrivateOffer }>(
    state => ({
      privateOffer: state.privateOffers.currentPrivateOffer[privateOfferId!],
    })
  )
  const productType = selectedProduct?.productType || PlanType.SAAS_CONTRACT
  const contractPricingModel = selectedProduct?.containerPricingModel as
    | ContainerPricingModel
    | undefined

  useEffect(() => {
    const isAccountReady = routeAccountId
      ? currentAccountId === routeAccountId
      : true

    if (!hasValidAccountId || isEmpty(productId) || !isAccountReady) {
      return
    }

    if (!selectedProduct && !productLoading) {
      const productsFetchKey = `products-${currentAccountId}-${selectedCloudType}`
      if (initiatedFetchRef.current === productsFetchKey) {
        return
      }
      initiatedFetchRef.current = productsFetchKey
      dispatch(getProducts(currentAccountId, selectedCloudType))
      return
    }

    if (selectedProduct && !isEmpty(privateOfferId) && isEmpty(privateOffer)) {
      const privateOfferFetchKey = `offer-${currentAccountId}-${privateOfferId}-${selectedCloudType}-${productId}-${crmOpportunityId || ''}`
      if (initiatedFetchRef.current === privateOfferFetchKey) {
        return
      }
      initiatedFetchRef.current = privateOfferFetchKey
      dispatch(
        getCurrentPrivateOffer(
          currentAccountId,
          privateOfferId || '',
          selectedCloudType,
          productId,
          crmOpportunityId
        )
      )
    }
  }, [
    hasValidAccountId,
    productId,
    selectedProduct,
    productLoading,
    routeAccountId,
    currentAccountId,
    selectedCloudType,
    privateOfferId,
    crmOpportunityId,
    privateOffer,
    dispatch,
  ])

  if (productLoading || (isOfferLoading && isEmpty(privateOffer))) {
    return <LoadingLogo />
  }

  const childProps: DetailedViewPrivateOfferProps = {
    privateOffer,
    planType: productType,
    externalBaseURL,
    contractPricingModel,
    crmOpportunityId,
    cloudMarketplace: selectedCloudType,
    search,
    product: selectedProduct,
    crmType,
    onBack,
    partnerId: partnerId || currentAccountId,
    users,
    viewTracking,
  }

  return isEmpty(privateOffer) ? (
    <div data-testid="empty-private-offer-wrapper"></div>
  ) : (
    <div>
      <DetailedViewPrivateOffer {...childProps} />
    </div>
  )
}
